import type { AddGroupLifeInsuranceDto } from "@/models/dto/groupLifeInsurance.dto";
import { api } from "../network/api";
import { ENDPOINTS } from "@/constants/endpoints";
import type { SaveComplianceEvaluationDto } from "@/models/dto/save-compliance-evaluation.dto";
import type { ECertificateStatus } from "@/constants/enums/certificate-status.enum";
import type { GenerateCertificateDto } from "@/models/dto/generate-certificate.dto";
import type { RespondToEvaluationDto } from "@/models/dto/respond-to-evaluation.dto";
import type { PaginationDto } from "@/models/dto/pagination.dto";
import type { CurrentYearFilterDto } from "@/models/dto/current-year-filter.dto";
import type { ContributionByMonthFilterDto } from "@/models/dto/current-year-filter.dto";
import type { PaginatedResponse } from "@/models/responses/base/baseResponse.response";
import type { EmployeeContribution } from "@/models/responses/compliance/employer-contributions.response";

export interface ComplianceCheckResult {
  name: string;
  status: "passed" | "failed" | "warning";
  icon: string;
  blockType?: ComplianceBlockType;
  blockCode?: string;
}

export type ComplianceBlockType = "HARD_BLOCK" | "SOFT_BLOCK";
export type ComplianceBlockUiColor = "RED" | "AMBER";

export interface ComplianceBlock {
  type: ComplianceBlockType;
  ui_color: ComplianceBlockUiColor;
  message: string;
  code: string;
}

const ComplianceService = {
  getContributionStats: async function () {
    return await api.get(ENDPOINTS.COMPLIANCE.CONTRIBUTION_STATS);
  },
  getEmployeeStats: async function () {
    return await api.get(ENDPOINTS.COMPLIANCE.EMPLOYEE_STATS);
  },
  getGroupLifeInsurance: async function () {
    return await api.get(ENDPOINTS.COMPLIANCE.GET_GROUP_LIFE_INSURANCE);
  },
  addGroupLifeInsurance: async function (dto: AddGroupLifeInsuranceDto) {
    const response = await api.post(
      ENDPOINTS.COMPLIANCE.ADD_GROUP_LIFE_INSURANCE,
      dto
    );
    return response.data;
  },
  updateGroupLifeInsurance: async function (dto: AddGroupLifeInsuranceDto) {
    const response = await api.patch(
      ENDPOINTS.COMPLIANCE.UPDATE_GROUP_LIFE_INSURANCE,
      dto
    );
    console.log({ response });
    return response.data;
  },
  evaluateCompliance: async function () {
    const response = await api.post(
      ENDPOINTS.COMPLIANCE.EVALUATE_COMPLIANCE,
      {}
    );
    return response.data;
  },
  saveComplianceEvaluation: async function (dto: SaveComplianceEvaluationDto) {
    const response = await api.post(
      ENDPOINTS.COMPLIANCE.SAVE_COMPLIANCE_EVALUATION,
      dto
    );
    return response.data;
  },
  getComplianceEvaluation: async function () {
    const response = await api.get(
      ENDPOINTS.COMPLIANCE.GET_COMPLIANCE_EVALUATION
    );
    return response.data;
  },

  // Parse decision trace into check results
  parseDecisionTrace: function (
    _decisionTrace: string[],
    failedConditions?: string[],
    blocks?: ComplianceBlock[]
  ): ComplianceCheckResult[] {
    if (blocks && Array.isArray(blocks) && blocks.length > 0) {
      return blocks.map((block) => {
        const status: ComplianceCheckResult["status"] =
          block.type === "HARD_BLOCK" ? "failed" : "warning";
        const icon =
          block.ui_color === "RED"
            ? "/assets/icons/round-cancel.svg"
            : "/assets/icons/alert-fill.svg";

        return {
          name: block.message || block.code,
          status,
          icon,
          blockType: block.type,
          blockCode: block.code,
        };
      });
    }

    // If failed_conditions exists and is not empty, use it to create failed check results
    if (
      failedConditions &&
      Array.isArray(failedConditions) &&
      failedConditions.length > 0
    ) {
      return failedConditions.map((condition: string) => ({
        name: condition,
        status: "failed",
        icon: "/assets/icons/round-cancel.svg",
      }));
    }

    // No blocks and no failed_conditions: show simple passed summary
    const checkIcon = "/assets/icons/check-white.svg";
    return [
      {
        name: "Valid Group Life Insurance",
        status: "passed" as const,
        icon: checkIcon,
      },
      {
        name: "Contributions up to date",
        status: "passed" as const,
        icon: checkIcon,
      },
    ];
  },

  generateCertificate: async function (dto: GenerateCertificateDto) {
    const response = await api.post(
      ENDPOINTS.COMPLIANCE.GENERATE_CERTIFICATE,
      dto
    );
    return response.data;
  },

  getCertificates: async function (status: ECertificateStatus) {
    const response = await api.get(
      ENDPOINTS.COMPLIANCE.GET_CERTIFICATES(status)
    );
    return response.data;
  },

  getCertificateById: async function (id: string) {
    const response = await api.get(
      ENDPOINTS.COMPLIANCE.GET_CERTIFICATE_BY_ID(id)
    );
    return response.data;
  },

  acknowledgeCertificateDisclaimer: async function (
    id: string,
    disclaimerVersion: string
  ) {
    const response = await api.post(
      ENDPOINTS.COMPLIANCE.ACKNOWLEDGE_DISCLAIMER(id),
      { disclaimerVersion }
    );
    return response.data;
  },

  /**
   * Re-render the certificate ahead of a download if its figures have changed
   * since the last render. Returns the certificate row to download from plus
   * whether a re-render happened. Long timeout because a stale certificate does
   * a full PDF render and storage round-trip server-side.
   */
  refreshCertificate: async function (id: string) {
    const response = await api.post(
      ENDPOINTS.COMPLIANCE.REFRESH_CERTIFICATE(id),
      {},
      { timeout: 120000 }
    );
    return response.data;
  },

  // Download PCC Certificate from verification URL
  downloadCertificateFromUrl: function (verificationUrl: string) {
    try {
      // Extract filename from URL or use default
      const urlParts = verificationUrl.split("/");
      const filename =
        urlParts[urlParts.length - 1] || `PCC_Certificate_${Date.now()}.pdf`;

      // Create a temporary link and trigger download
      // This approach works with CORS-protected URLs since we're not fetching the content
      const link = document.createElement("a");
      link.href = verificationUrl;
      link.download = filename;
      link.target = "_blank"; // Open in new tab as fallback
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading certificate:", error);
      throw error;
    }
  },
  getEvaluationComments: async function (evaluationId: string) {
    const response = await api.get(
      ENDPOINTS.COMPLIANCE.GET_EVALUATION_COMMENTS(evaluationId)
    );
    return response.data;
  },
  respondToEvaluation: async function (dto: RespondToEvaluationDto) {
    console.log({ dto });
    const response = await api.post(
      ENDPOINTS.COMPLIANCE.RESPOND_TO_EVALUATION,
      dto
    );
    return response.data;
  },
  getContributionTotal: async function (employerCode: string) {
    const response = await api.get(
      ENDPOINTS.COMPLIANCE.CONTRIBUTION_TOTAL(employerCode)
    );
    return response.data;
  },
  getEmployeeContributions: async function (dto: PaginationDto) {
    const response = await api.get(
      ENDPOINTS.COMPLIANCE.EMPLOYEE_CONTRIBUTIONS,
      { params: dto }
    );
    return response.data;
  },
  getEmployeeCount: async function () {
    const response = await api.get(ENDPOINTS.COMPLIANCE.EMPLOYEE_COUNT);
    return response.data;
  },
  getCurrentMonthReport: async function () {
    const response = await api.get(ENDPOINTS.COMPLIANCE.CURRENT_MONTH_REPORTS);
    return response;
  },
  getCurrentYearReports: async function (dto?: CurrentYearFilterDto) {
    const response = await api.get(ENDPOINTS.COMPLIANCE.CURRENT_YEAR_REPORTS, {
      params: dto,
    });
    return response;
  },
  getPenaltyStatus: async function (day: string) {
    const response = await api.get(ENDPOINTS.COMPLIANCE.PENALTY_STATUS, {
      params: { day },
    });
    return response.data;
  },
  getContributionByMonth: async function (dto: ContributionByMonthFilterDto) {
    const response = await api.get(ENDPOINTS.COMPLIANCE.CONTRIBUTION_BY_MONTH, {
      params: dto,
    });
    return response as unknown as PaginatedResponse<EmployeeContribution>;
  },
  getTasks: async function () {
    const response = await api.get(ENDPOINTS.COMPLIANCE.TASKS);
    return response.data;
  },
  getPenalties: async function () {
    const response = await api.get(ENDPOINTS.COMPLIANCE.PENALTIES);
    return response.data;
  },
  getBanners: async function () {
    const response = await api.get(ENDPOINTS.COMPLIANCE.BANNERS);
    return response.data;
  },
  getPenaltiesSchedulePdf: async function (): Promise<Blob> {
    const data = await api.get(ENDPOINTS.COMPLIANCE.PENALTIES_SCHEDULE_PDF, {
      responseType: "blob",
      timeout: 120000,
    });
    return data as unknown as Blob;
  },
  getPenaltiesBreakdown: async function (month: string, year: string) {
    return await api.get(ENDPOINTS.COMPLIANCE.PENALTIES_BREAKDOWN(month, year));
  },
  getPenaltiesScheduleXlsx: async function (): Promise<Blob> {
    const data = await api.get(ENDPOINTS.COMPLIANCE.PENALTIES_SCHEDULE_XLSX, {
      responseType: "blob",
      timeout: 120000,
    });
    return data as unknown as Blob;
  },
  getContributionsScheduleXlsx: async function (): Promise<Blob> {
    const data = await api.get(
      ENDPOINTS.COMPLIANCE.CONTRIBUTIONS_SCHEDULE_XLSX,
      {
        responseType: "blob",
        timeout: 120000,
      }
    );
    return data as unknown as Blob;
  },

  // Get the payment schedule for the current month
  getPaymentSchedule: async function () {
    const response = await api.get(ENDPOINTS.COMPLIANCE.SCHEDULE);
    return response.data;
  },
};

export default ComplianceService;
